import { Component } from '@angular/core';
import { take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { RoleplayApiService } from '../../service/roleplay.service';
import { InteractionService } from '../../service/interaction/interaction.service';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { CoachResponseDialogComponent } from '../assigned-roleplay/coach-response-dialog/coach-response-dialog.component';
import { ReferenceDocumentService } from '../../service/reference-document.service';
import { KeyVaultService } from '../../service/key-vault.service';
import { MarkdownModule } from 'ngx-markdown';

import html2pdf from 'html2pdf.js';
import { marked } from 'marked';
import { ToastrService } from 'ngx-toastr';
import { TruncatePipe } from '../../service/truncate.pipe';
import { UserFeedbackComponent } from '../../v2/shared/components/ui/user-feedback/user-feedback.component';

type Conversation = {
  question: string;
  answer: string;
  getCurrentQuesAnsTime: string;
  coach_used?: boolean;
  bot_last_response?: string;
  coach_references?: any[];
};

@Component({
  selector: 'app-roleplay-history',
  standalone: true,
  imports: [
    CommonModule,
    TruncatePipe,
    MarkdownModule
  ],
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss'
})
export class RoleplayHistoryComponent {
  chat_session_id: number;
  history: any;
  conversations: Conversation[] = [];
  givenName = sessionStorage.getItem("givenName");
  ask_user_feedback: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private rolePlayService: RoleplayApiService,
    private datePipe: DatePipe,
    private intrService: InteractionService,
    private dialog: MatDialog,
    private router: Router,
    private toastr: ToastrService,
    private referenceDocumentService: ReferenceDocumentService,
    private keyVaultService: KeyVaultService
  ) { }

  ngOnInit() {
    this.setupSubscribers();
    this.keyVaultService.apimUrl$.pipe(take(1)).subscribe(url => this.referenceDocumentService.setApimUrl(url));
  }

  setupSubscribers() {
    this.route.paramMap.subscribe(params => {
      const chat_session_id = +params.get('chat_session_id');
      if (chat_session_id) {
        this.chat_session_id = +chat_session_id;
        this.getRoleplayHistory(this.chat_session_id);
      } else {
        console.error('chat_session_id is undefined. Check if the route is configured with the correct parameter.');
      }
    });

    //watch queryparams here
    // Component is reused across sessions, so the flag must be recomputed on every navigation.
    this.route.queryParamMap.subscribe(params => {
      this.ask_user_feedback = !!params.get('user_feedback');
    });
  }

  getRoleplayHistory(sessionId) {
    this.intrService.hideComponentHandler({ hideHeader: false, hideSidebar: false, hideFullHeader: true })
    this.rolePlayService.setVisibility(false);
    this.rolePlayService.chatRoleplayHistoryList(sessionId).subscribe((data) => {
      this.history = data;
      this.mapResponseToConversations(data.conversations);
      this.checkAndOpenFeedbackDialog();
    });
  }

  mapResponseToConversations(response: any[]) {
    this.conversations.length = 0;
    if (response.length > 0) {
      response.forEach((data: any) => {
        const localRequestTime = this.convertUtcToLocal(data.request_time);
        let conversation: Conversation = {
          question: data.user_request,
          answer: data.bot_response,
          getCurrentQuesAnsTime: this.datePipe.transform(localRequestTime, 'hh:mm MMM dd'),
          coach_used: data.coach_used || false,
          bot_last_response: data.bot_last_response || '',
          coach_references: data.coach_references || null
        };
        this.conversations.push(conversation);
      });
    }
  }

  checkAndOpenFeedbackDialog(){
    if (this.conversations.length >= 1 && this.ask_user_feedback) {
      this.ask_user_feedback = false;
      this.openUserFeedbackDialog();
    }
  }

  convertUtcToLocal(utcDateString: string): Date | null {
    return new Date(this.datePipe.transform(utcDateString.split('.')[0] + 'Z', 'yyyy-MM-dd HH:mm:ss'));
  }

  openFeedbackScreen() {
    if (this.chat_session_id) {
      this.rolePlayService.getRoleplayFeedbackSessionId(this.chat_session_id).subscribe((data: any) => {
        if (data.message && data.message.trim() !== '') {
          this.showFeedbackMessage(data.message);
        } else {
          this.redirectToFeedback();
        }
      });
    }
  }

  showFeedbackMessage(message: string) {
    this.dialog.open(MessageDialogComponent, {
      data: { message: message },
      width: '400px',
    });
  }

  redirectToFeedback() {
    this.router.navigate([`/app-roleplay-feedback/${this.chat_session_id}`], {
      skipLocationChange: true,
    });
  }

  displayTranscriptAsPdf() {
    if (this.conversations.length === 0 || !this.chat_session_id) {
      this.toastr.info('No transcription is available to download', '', { positionClass: 'toast-top-right' });
      return;
    }
    const transcriptHtml = this.conversations.map((chat: any) => {
      const responseHtml = chat.person === 'fieldRep'
        ? marked.parse(chat.response)
        : chat.response;
      return `
         <div style="margin-bottom: 10px;">
          <div style="display: block; margin-bottom: 5px;">${this.givenName} -${chat.question}</div>
          <div style="display: block;">${this.history.dr_name} -${chat.answer}</div>
        </div>
        `;
    }).join('');
    const htmlContent = `
        <div>
          <h1>Transcript</h1>
          ${transcriptHtml}
        </div>
      `;
    const options = {
      margin: 0.5,
      filename: `roleplay_transcript_${this.chat_session_id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().from(htmlContent).set(options).save(); // Directly save the PDF file
  }

  viewCoachResponse(conversation: Conversation) {
    this.dialog.open(CoachResponseDialogComponent, {
      width: '500px',
      data: {
        coachResponse: conversation.bot_last_response,
        timestamp: conversation.getCurrentQuesAnsTime
      }
    });
  }

  openReferenceDocument(ref: any, event?: MouseEvent) {
    this.referenceDocumentService.openReferenceDocument(ref, event);
  }

  openUserFeedbackDialog() {
    this.dialog.open(UserFeedbackComponent, {
      data: {
        chat_session_id: this.chat_session_id
      },
      width: '800px',
    });
  }
}
